import React from 'react';
import { projectGroups, analyticsArchive } from '../data/projects';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

/**
 * Research-grade Projects & Skills Section
 * Refactored to use Tailwind CSS and match site-wide max-w-5xl width.
 */

function SkillsAndProjects() {
  return (
    <section
      id="skills-projects"
      className="relative py-8"
      style={{ backgroundColor: 'var(--bg-surface)' }}
    >
      <div className="container">
        <div className="row">
          <div className="col-sm-12">
            <div className="box-shadow-full">

              {/* ---------- HEADER ---------- */}
              <div className="title-box text-center section-header-reveal mb-12">
                <h2
                  className="text-3xl md:text-4xl font-bold mb-4 tracking-tight"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Projects
                </h2>
                <div
                  className="w-16 h-1 mx-auto mb-6 rounded-full"
                  style={{ backgroundColor: 'var(--primary)' }}
                />
                <p
                  className="text-lg leading-relaxed max-w-2xl mx-auto"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  A curated selection of research-oriented and analytical projects
                  representing my technical development and research trajectory.
                </p>
              </div>

              {/* ---------- PROJECT GROUPS ---------- */}
              <div className="max-w-5xl mx-auto px-4">
                {projectGroups.map((group) => (
                  <div key={group.id} className="mb-16 last:mb-0">

                    {/* Group Header with Line */}
                    <div className="flex items-center gap-4 mb-8">
                      <span
                        className="text-xs font-bold uppercase tracking-widest whitespace-nowrap"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        {group.title}
                      </span>
                      <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
                    </div>

                    {/* Projects Grid - 2 Columns on Large Screens */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {group.projects.map((project) => (
                        <a
                          key={project.id}
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group block p-6 rounded-lg border transition-all duration-300 hover:-translate-y-1 project-card"
                          style={{
                            backgroundColor: 'var(--bg-card)',
                            borderColor: 'var(--border)',
                            textDecoration: 'none'
                          }}
                        >
                          <div className="flex flex-col h-full">
                            <div className="flex justify-between items-start mb-3">
                              <h4
                                className="text-lg font-bold group-hover:text-blue-600 transition-colors"
                                style={{ color: 'var(--text-primary)' }}
                              >
                                {project.title}
                              </h4>
                              <FontAwesomeIcon
                                icon={faExternalLinkAlt}
                                className="text-xs opacity-40 group-hover:opacity-100 transition-opacity mt-1.5"
                                style={{ color: 'var(--text-muted)' }}
                              />
                            </div>

                            <p
                              className="text-sm leading-relaxed mb-4 flex-grow"
                              style={{ color: 'var(--text-secondary)' }}
                            >
                              {project.description}
                            </p>

                            <div className="flex items-center mt-auto">
                              <span
                                className="text-xs font-medium px-2.5 py-1 rounded-md"
                                style={{
                                  backgroundColor: 'var(--bg-surface)',
                                  color: 'var(--text-muted)',
                                  border: '1px solid var(--border)'
                                }}
                              >
                                {project.meta.split(' · ')[0]}
                              </span>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}

                {/* ---------- ANALYTICS ARCHIVE ---------- */}
                <div className="mt-16 pt-8 border-t" style={{ borderColor: 'var(--border)' }}>
                  <a
                    href={analyticsArchive.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block p-8 rounded-lg border transition-all duration-300 hover:shadow-lg project-card"
                    style={{
                      backgroundColor: 'var(--bg-card)',
                      borderColor: 'var(--border)',
                      textDecoration: 'none'
                    }}
                  >
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                      <div>
                        <h3
                          className="text-xl font-bold mb-2"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {analyticsArchive.title}
                        </h3>
                        <p
                          className="text-sm leading-relaxed max-w-xl"
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          {analyticsArchive.description}
                        </p>
                      </div>
                      <div
                        className="flex items-center gap-2 font-semibold whitespace-nowrap group-hover:translate-x-1 transition-transform"
                        style={{ color: 'var(--primary)' }}
                      >
                        <span>View Archive</span>
                        <FontAwesomeIcon icon={faArrowRight} />
                      </div>
                    </div>
                  </a>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default SkillsAndProjects;
